import { useTranslation } from 'react-i18next';
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { Button } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/utils/cn";
import type { TourStepDefinition } from "@/core/tour/tourConfig";

interface QuickStartPanelProps {
  steps: TourStepDefinition[];
  completed: Record<string, boolean>;
  detecting: boolean;
  onDismiss: () => void;
}

export function QuickStartPanel({
  steps,
  completed,
  detecting,
  onDismiss,
}: QuickStartPanelProps) {
  const { t } = useTranslation('common');
  const [collapsed, setCollapsed] = useState(false);

  const doneCount = steps.filter((step) => completed[step.id]).length;
  const progress =
    steps.length > 0 ? Math.round((doneCount / steps.length) * 100) : 0;
  const activeIndex = steps.findIndex((step) => !completed[step.id]);
  const activeStep = activeIndex >= 0 ? steps[activeIndex] : null;

  const stepLabels = useMemo(
    () => steps.map((step) => step.title.split(" ").slice(-1)[0] ?? step.title),
    [steps],
  );

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="quick-start-fab"
        aria-label={t('labels.expandQuickStartChecklist')}
      >
        <RocketLaunchIcon fontSize="small" />
        <span className="quick-start-fab-label">{progress}%</span>
      </button>
    );
  }

  return (
    <aside className="quick-start-panel" aria-label={t('labels.quickStartChecklist')}>
      <div className="quick-start-panel-header">
        <div className="flex items-start gap-3">
          <span className="quick-start-panel-icon">
            <RocketLaunchIcon fontSize="small" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 dark:text-primary-400">
              Quick start
            </p>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Get productive fast
            </p>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {detecting
                ? "Checking your progress…"
                : `${doneCount} of ${steps.length} complete`}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Tooltip content="Minimize">
            <button
              type="button"
              className="quick-start-icon-btn"
              onClick={() => setCollapsed(true)}
              aria-label={t('labels.minimizeQuickStart')}
            >
              <UnfoldLessIcon sx={{ fontSize: 18 }} />
            </button>
          </Tooltip>
          <Tooltip content="Dismiss">
            <button
              type="button"
              className="quick-start-icon-btn"
              onClick={onDismiss}
              aria-label={t('labels.dismissQuickStart')}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="quick-start-progress-track" aria-hidden>
        <div
          className="quick-start-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="quick-start-step-pills" aria-hidden>
        {steps.map((step, index) => {
          const done = Boolean(completed[step.id]);
          const current = index === activeIndex;
          return (
            <Tooltip key={step.id} content={step.title}>
              <span
                className={cn(
                  "quick-start-step-pill",
                  done && "quick-start-step-pill--done",
                  current && "quick-start-step-pill--active",
                )}
              >
                {stepLabels[index]}
              </span>
            </Tooltip>
          );
        })}
      </div>

      {activeStep && (
        <div className="quick-start-active-card">
          <p className="text-xs font-medium uppercase tracking-wide text-primary-600 dark:text-primary-400">
            Up next
          </p>
          <p className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
            {activeStep.title}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {activeStep.description}
          </p>
          <Link to={activeStep.actionTo} className="quick-start-primary-link">
            {activeStep.actionLabel}
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Link>
        </div>
      )}

      <ul className="quick-start-step-list">
        {steps.map((step, index) => {
          const done = Boolean(completed[step.id]);
          const current = index === activeIndex;
          return (
            <li
              key={step.id}
              className={cn(
                "quick-start-step-row",
                done && "quick-start-step-row--done",
                current && "quick-start-step-row--active",
              )}
            >
              <span className="quick-start-step-marker" aria-hidden>
                {done ? (
                  <CheckCircleIcon
                    className="text-primary-600 dark:text-primary-400"
                    sx={{ fontSize: 20 }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    className="text-slate-300 dark:text-slate-600"
                    sx={{ fontSize: 20 }}
                  />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {step.title}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {step.description}
                </p>
              </div>
              {!done && (
                <Link to={step.actionTo} className="quick-start-step-link">
                  Go
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      {doneCount === steps.length && (
        <p className="quick-start-complete-banner">
          All set — you are ready to go!
        </p>
      )}

      <Button
        type="button"
        variant="ghost"
        className="mt-3 w-full !text-xs"
        onClick={onDismiss}
      >
        Hide checklist
      </Button>
    </aside>
  );
}
